import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getGapAnalysisDashboard } from '../../../services/gap-analysis';
import { useNavigate, useParams } from 'react-router-dom';
import { typeLabels, statusControl, maturityLevels } from '../../../utils/labels_utils';
import { tableHeader } from './table-header';
import { tableControls } from './table-controls';
import { GapDashboardView } from '../../../Components/GapDashboardView';

const ENTITY_ORDER = ['companies', 'areas', 'processes', 'systems', 'assets'];
const ENTITY_COLOR_MAP = {
  companies: 'var(--vo-cobalto)',
  areas:     'var(--vo-warning)',
  processes: 'var(--vo-celeste)',
  systems:   'var(--vo-capri)',
  assets:    'var(--vo-success)',
};
const MATURITY_COLORS_LIST = ['var(--vo-danger)', 'var(--vo-warning)', 'var(--vo-capri)', 'var(--vo-celeste)', 'var(--vo-success)', 'var(--vo-cobalto)'];

const sortedEntityKeys = obj =>
  ENTITY_ORDER.filter(k => k in obj);

const getStatusLabel = (key, t) => {
  const sc = statusControl[key];
  if (!sc) return key;
  if (sc.labelKey) return t(sc.labelKey);
  return sc.label || key;
};

export const GapAnalysisDashboard = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();

  const { isLoading, error, data: d } = useQuery('getGapAnalysisDashboard', async () => {
    const res = await getGapAnalysisDashboard(id);
    return res.status === 200 ? res.data : null;
  });

  if (isLoading) return <p>{t('common:labels.loading')}</p>;
  if (error) return <p>{t('gap-analysis:controlsLink.error')}</p>;

  const statusDonutData = Object.keys(d.statusCounts || {})
    .map(key => ({
      name: getStatusLabel(key, t),
      value: d.statusCounts[key] || 0,
      color: statusControl[key]?.color || 'var(--vo-slate)',
    }))
    .filter(item => item.value > 0);

  const maturityData = Object.keys(maturityLevels).map((key, i) => ({
    name: maturityLevels[key],
    value: d.maturityCounts?.[key] || 0,
    color: MATURITY_COLORS_LIST[i % MATURITY_COLORS_LIST.length],
  }));

  const entityDonutData = sortedEntityKeys(d.entityCount || {}).map(key => ({
    name: typeLabels[key] || key,
    value: d.entityCount[key] || 0,
    color: ENTITY_COLOR_MAP[key] || 'var(--vo-slate)',
  }));

  const stackedData = sortedEntityKeys(d.entityCountsByTypeWithStatus || {}).map(key => ({
    name: typeLabels[key] || key,
    statuses: d.entityCountsByTypeWithStatus[key] || {},
  }));

  const entityProgressRaw = d.entityTypeProcess || {};
  const entityProgressData = Object.values(entityProgressRaw)
    .map(item => ({
      label: typeLabels[item.label] || item.label || '',
      value: item.percentage ?? 0,
      _ord: ENTITY_ORDER.indexOf(item.label),
    }))
    .sort((a, b) => (a._ord < 0 ? 999 : a._ord) - (b._ord < 0 ? 999 : b._ord));

  const frameworkData = (d.progressoPorFramework || []).map(item => ({
    label: item.name,
    value: Math.round(item.progress || 0),
  }));

  const entitiesByType = d.entitiesByType || {};
  const tabs = sortedEntityKeys(entitiesByType).map(type => ({ id: type, label: typeLabels[type] || type }));

  return (
    <GapDashboardView
      title={d.gapAnalysis?.name ?? ''}
      segments={[
        { label: t('common:breadcrumbs.analysis') },
        { label: t('gap-analysis:page.title'), route: '/gap-analysis' },
        { label: d.gapAnalysis?.name ?? '', route: `/gap-analysis/show/${id}` },
        { label: t('gap-analysis:show.dashboardButton') },
      ]}
      backLabel={t('gap-analysis:dashboard.backButton')}
      onBack={() => navigate(`/gap-analysis/show/${id}`)}
      overallProgress={Math.min(d.overallProgress ?? 0, 100)}
      linkedControls={d.linkedControls ?? 0}
      totalControls={d.totalControls ?? 0}
      historicalProgress={d.historicalProgress}
      frameworkData={frameworkData}
      statusDonutData={statusDonutData}
      maturityData={maturityData}
      entityDonutData={entityDonutData}
      stackedData={stackedData}
      entityProgressData={entityProgressData}
      uniqueEntityCount={d.uniqueEntityCount ?? 0}
      entitiesByType={entitiesByType}
      tabs={tabs}
      tableLink={`gap-analysis/${id}`}
      tableLinkExtend="gap-analysis/control/show"
      tableHeaderFn={tableHeader}
      tableControlsFn={tableControls}
      getTypeLabel={type => typeLabels[type] || type}
    />
  );
};
